import React from 'react';
import Box from '@mui/material/Box';
import {
    Button, Checkbox, Chip, Divider, MenuItem, Paper, Popover,
    Select, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import FilterListIcon from '@mui/icons-material/FilterList';

/**
 * Barre d'outils principale : saisie de la localisation, bouton de chargement,
 * sélecteur de sources, export CSV et sélecteur de séparateur.
 */
export default function DataToolbar({
    localisation, setLocalisation,
    typeLoc, setTypeLoc,
    loading, csvData,
    rows, columns,
    csvSeparator, setCsvSeparator,
    onLoad, onExport,
    availableSources, selectedSources, setSelectedSources,
}) {
    const [sourcesAnchor, setSourcesAnchor] = React.useState(null);

    const allSelected  = availableSources.length > 0 && selectedSources.size === availableSources.length;
    const someSelected = selectedSources.size > 0 && !allSelected;

    const toggleSource = (key, checked) => {
        const next = new Set(selectedSources);
        checked ? next.add(key) : next.delete(key);
        setSelectedSources(next);
    };

    return (
        <Paper elevation={1} sx={{
            mx: 2, mt: 2, px: 2, py: 1.5, borderRadius: 2,
            display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap',
        }}>
            <TextField
                label="Code localisation" size="small" disabled={loading}
                value={localisation} onChange={e => setLocalisation(e.target.value)}
                sx={{width: 180}}
            />

            <Select size="small" sx={{width: 180}} disabled={loading}
                    value={typeLoc} onChange={e => setTypeLoc(e.target.value)}
            >
                <MenuItem value="commune">Commune</MenuItem>
                <MenuItem value="departement">Département</MenuItem>
                <MenuItem value="region">Région</MenuItem>
                <MenuItem value="epci" disabled>EPCI</MenuItem>
            </Select>

            <Button variant="contained" disabled={loading} startIcon={<SearchIcon/>}
                    onClick={onLoad}
                    sx={{borderRadius: 2, textTransform: 'none', fontWeight: 'bold'}}
            >
                Charger
            </Button>

            <Divider orientation="vertical" flexItem/>

            {/* ── Sélecteur de sources ──────────────────────────────────────── */}
            <Tooltip title={!csvData ? 'Chargez des données pour filtrer les sources' : 'Choisir les sources à afficher et exporter'}>
                <span>
                    <Button
                        variant={someSelected ? 'contained' : 'outlined'}
                        color={someSelected ? 'warning' : 'primary'}
                        disabled={!csvData || availableSources.length === 0}
                        startIcon={<FilterListIcon/>}
                        onClick={e => setSourcesAnchor(e.currentTarget)}
                        sx={{borderRadius: 2, textTransform: 'none'}}
                    >
                        Sources{csvData ? ` (${selectedSources.size}/${availableSources.length})` : ''}
                    </Button>
                </span>
            </Tooltip>

            <Popover
                open={Boolean(sourcesAnchor)}
                anchorEl={sourcesAnchor}
                onClose={() => setSourcesAnchor(null)}
                anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
                slotProps={{paper: {sx: {borderRadius: 2, mt: 0.5}}}}
            >
                <Box sx={{p: 1.5, minWidth: 230}}>
                    <Box sx={{display: 'flex', gap: 1, mb: 1}}>
                        <Button size="small" variant="outlined" sx={{flex: 1, textTransform: 'none', fontSize: '0.75rem'}}
                                onClick={() => setSelectedSources(new Set(availableSources.map(s => s.key)))}
                        >
                            Tout sélectionner
                        </Button>
                        <Button size="small" variant="outlined" color="inherit"
                                sx={{flex: 1, textTransform: 'none', fontSize: '0.75rem'}}
                                onClick={() => setSelectedSources(new Set())}
                        >
                            Tout désélectionner
                        </Button>
                    </Box>

                    <Divider sx={{mb: 0.5}}/>

                    {availableSources.map(s => (
                        <Box key={s.key} sx={{display: 'flex', alignItems: 'center', gap: 0.5, py: 0.2}}>
                            <Checkbox
                                size="small"
                                checked={selectedSources.has(s.key)}
                                onChange={e => toggleSource(s.key, e.target.checked)}
                                sx={{'& .MuiSvgIcon-root': {fontSize: 18, color: s.color}}}
                            />
                            <Box sx={{width: 10, height: 10, borderRadius: '50%', bgcolor: s.color, flexShrink: 0}}/>
                            <Typography variant="body2" sx={{ml: 0.5}}>{s.label}</Typography>
                        </Box>
                    ))}
                </Box>
            </Popover>

            <Divider orientation="vertical" flexItem/>

            <Tooltip title={!csvData ? 'Aucune donnée à exporter' : 'Télécharger en CSV (sources sélectionnées)'}>
                <span>
                    <Button variant="outlined" disabled={!csvData || selectedSources.size === 0}
                            startIcon={<DownloadIcon/>}
                            onClick={onExport} sx={{borderRadius: 2, textTransform: 'none'}}
                    >
                        Exporter CSV
                    </Button>
                </span>
            </Tooltip>

            <Tooltip title="Séparateur CSV">
                <ToggleButtonGroup size="small" exclusive value={csvSeparator} sx={{height: 36}}
                                   onChange={(_, val) => { if (val) setCsvSeparator(val); }}
                >
                    {[';', ',', '\\t', '|'].map(s => (
                        <ToggleButton key={s} value={s}
                                      sx={{px: 1.5, textTransform: 'none', fontSize: '0.78rem', fontFamily: 'monospace'}}
                        >
                            {s === '\\t' ? '⇥' : s}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            </Tooltip>

            {csvData && !loading && (
                <Chip
                    label={`${rows.length} adresse(s) · ${columns.length - 1} col.`}
                    color="primary" variant="outlined" size="small"
                    sx={{ml: 'auto', fontWeight: 'bold'}}
                />
            )}
        </Paper>
    );
}
